import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import useStore from "@/store";
import dayjs from "dayjs";
import { useRef } from "react";
import { BsInfoLg } from "react-icons/bs";
import { useReactToPrint } from "react-to-print";
import { Button } from "../../../components/ui/Button";
import PrintCard from "./PrintCard";
import { useTranslation } from "react-i18next";
import resolveOrderInfoMessage from "@/utils/resolveOrderInfoMessage";
import PrintActivatedCard from "./PrintActivatedCard";

const MoreInfoPopover = ({ order }) => {
  const { t } = useTranslation("dashboard");
  const componentRef = useRef();
  const handlePrint = useReactToPrint({ content: () => componentRef.current });
  const currentUser = useStore(state => state.currentUser);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="icon" variant="ghost" className="h-8 w-8">
          <BsInfoLg className="text-[1.1rem]" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end">
        <span className="font-medium">{t("popovers.more_info.title")}</span>

        <div className="flex flex-col gap-1 text-sm py-4">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">
              {t("popovers.more_info.code")} :
            </span>
            <span>{order?.itemCode}</span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">
              {t("popovers.more_info.expiry_date")} :
            </span>
            <span>{dayjs(order?.itemExpiration).format("YYYY-MM-DD")}</span>
          </div>

          {order?.orderInfoPhone && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">
                {t("popovers.more_info.phone_number")}:
              </span>
              <span>{order.orderInfoPhone}</span>
            </div>
          )}
          {order?.itemSerialNumber && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">
                {t("popovers.more_info.serial_number")}:
              </span>
              <span>{order.itemSerialNumber}</span>
            </div>
          )}
          {order?.orderInfoTransaction && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">
                {t("popovers.more_info.transaction_id")}:
              </span>
              <span>{order.orderInfoTransaction}</span>
            </div>
          )}
          {order?.orderInfoClientId && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">
                {t("popovers.more_info.client_id")}:
              </span>
              <span>{order.orderInfoClientId}</span>
            </div>
          )}
          {order?.orderInfoPlayerId && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">
                {t("popovers.more_info.player_id")}:
              </span>
              <span>{order.orderInfoPlayerId}</span>
            </div>
          )}
          {order?.orderInfoPlayerName && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">
                {t("popovers.more_info.player_name")}:
              </span>
              <span>{order.orderInfoPlayerName}</span>
            </div>
          )}
          {order?.orderInfoRegion && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">
                {t("popovers.more_info.region")}:
              </span>
              <span>{order.orderInfoRegion}</span>
            </div>
          )}
          {order?.orderInfoOperationId && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">
                {t("popovers.more_info.operation_id")}:
              </span>
              <span>{order.orderInfoOperationId}</span>
            </div>
          )}
          {order?.orderInfoDisplayId && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">
                {t("popovers.more_info.display_id")}:
              </span>
              <span>{order.orderInfoDisplayId}</span>
            </div>
          )}
          {resolveOrderInfoMessage(t, order?.orderInfoMessage, order) && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">
                {t("popovers.more_info.message")}:
              </span>
              <span>{resolveOrderInfoMessage(t, order?.orderInfoMessage, order)}</span>
            </div>
          )}

          <div className="hidden">
            {order.itemActivated ? (
              <PrintActivatedCard
                serialNumber={order.itemSerialNumber}
                code={order.itemCode}
                phone={order.orderInfoPhone}
                transactionId={order.orderInfoTransaction}
                clientId={order.orderInfoClientId}
                cardName={order.itemProductName}
                ref={componentRef}
              />
            ) : (
              <PrintCard
                ref={componentRef}
                currentUser={
                  currentUser?.user?.fname + " " + currentUser?.user?.lname
                }
                product={order?.itemProductName}
                code={order?.itemCode}
                exp={order?.itemExpiration}
                serialNumber={order?.itemSerialNumber}
              />
            )}
          </div>
        </div>

        <Button onClick={handlePrint} size="sm" className="w-full">
          {t("popovers.more_info.print")}
        </Button>
      </PopoverContent>
    </Popover>
  );
};

export default MoreInfoPopover;
